/**
 * URL Utilities
 * Handles URL parsing, formatting, and internationalized domain name (IDN) display
 */

/**
 * Decodes a Punycode domain to its Unicode display form
 * Example: "xn--b1aekbb1acci5f.com" → "почивнидни.com"
 *
 * @param hostname - The hostname which may be in Punycode format (xn--...)
 * @returns The Unicode display form of the hostname
 */
export function decodePunycodeHostname(hostname: string): string {
  if (!hostname) return hostname;

  // If hostname doesn't contain punycode-encoded labels, return as-is
  if (!hostname.includes('xn--')) {
    return hostname;
  }

  try {
    // Split the hostname into labels (parts separated by dots)
    const labels = hostname.split('.');

    // Decode each label that starts with 'xn--'
    const decodedLabels = labels.map(label => {
      if (label.toLowerCase().startsWith('xn--')) {
        return decodePunycodeLabel(label);
      }
      return label;
    });

    return decodedLabels.join('.');
  } catch {
    // If decoding fails, return the original hostname
    return hostname;
  }
}

/**
 * Decodes a single Punycode label (without the xn-- prefix handling the full algorithm)
 * Implements the Punycode decoding algorithm per RFC 3492
 *
 * @param label - A Punycode-encoded label starting with "xn--"
 * @returns The decoded Unicode string
 */
function decodePunycodeLabel(label: string): string {
  if (!label.toLowerCase().startsWith('xn--')) {
    return label;
  }

  // Remove the 'xn--' prefix
  const encoded = label.slice(4);

  // Punycode parameters
  const base = 36;
  const tMin = 1;
  const tMax = 26;
  const skew = 38;
  const damp = 700;
  const initialBias = 72;
  const initialN = 128;

  const output: number[] = [];
  let bias = initialBias;
  let n = initialN;
  let i = 0;

  // Find the last delimiter (hyphen)
  const delimiterIndex = encoded.lastIndexOf('-');

  // Copy the basic characters (everything before the last delimiter)
  if (delimiterIndex > 0) {
    for (let j = 0; j < delimiterIndex; j++) {
      output.push(encoded.charCodeAt(j));
    }
  }

  // Start processing after the delimiter (or from the beginning if no delimiter)
  let index = delimiterIndex > 0 ? delimiterIndex + 1 : 0;

  while (index < encoded.length) {
    const oldI = i;
    let w = 1;
    let k = base;

    while (true) {
      if (index >= encoded.length) {
        throw new Error('Invalid Punycode');
      }

      const digit = decodeDigit(encoded.charCodeAt(index++));
      if (digit >= base) {
        throw new Error('Invalid Punycode');
      }

      i += digit * w;

      const t = k <= bias ? tMin : k >= bias + tMax ? tMax : k - bias;

      if (digit < t) {
        break;
      }

      w *= base - t;
      k += base;
    }

    const outLen = output.length + 1;
    bias = adapt(i - oldI, outLen, oldI === 0, damp, skew, base, tMin, tMax);
    n += Math.floor(i / outLen);
    i %= outLen;

    // Insert the decoded character
    output.splice(i, 0, n);
    i++;
  }

  return String.fromCodePoint(...output);
}

/**
 * Decode a single Punycode digit
 */
function decodeDigit(codePoint: number): number {
  if (codePoint - 48 < 10) {
    return codePoint - 22; // 0-9 → 26-35
  }
  if (codePoint - 65 < 26) {
    return codePoint - 65; // A-Z → 0-25
  }
  if (codePoint - 97 < 26) {
    return codePoint - 97; // a-z → 0-25
  }
  return 36; // Invalid
}

/**
 * Adapt the bias for Punycode algorithm
 */
function adapt(
  delta: number,
  numPoints: number,
  firstTime: boolean,
  damp: number,
  skew: number,
  base: number,
  tMin: number,
  tMax: number
): number {
  delta = firstTime ? Math.floor(delta / damp) : Math.floor(delta / 2);
  delta += Math.floor(delta / numPoints);

  let k = 0;
  while (delta > Math.floor(((base - tMin) * tMax) / 2)) {
    delta = Math.floor(delta / (base - tMin));
    k += base;
  }

  return k + Math.floor(((base - tMin + 1) * delta) / (delta + skew));
}

/**
 * Extracts and decodes the hostname from a URL, returning the Unicode display form
 *
 * @param url - The full URL string
 * @returns The Unicode hostname or the original URL if parsing fails
 */
export function getDisplayHostname(url: string): string {
  try {
    const parsedUrl = new URL(url);
    return decodePunycodeHostname(parsedUrl.hostname);
  } catch {
    // If URL parsing fails, try to extract hostname manually
    const match = url.match(/^(?:https?:\/\/)?([^:/\s]+)/i);
    if (match) {
      return decodePunycodeHostname(match[1]);
    }
    return url;
  }
}

/**
 * Formats a URL for display, converting Punycode domains to Unicode
 *
 * @param url - The full URL string
 * @returns The URL with Unicode hostname for display
 */
export function getDisplayUrl(url: string): string {
  try {
    const parsedUrl = new URL(url);
    const unicodeHostname = decodePunycodeHostname(parsedUrl.hostname);

    // Replace the punycode hostname with the unicode one
    return url.replace(parsedUrl.hostname, unicodeHostname);
  } catch {
    return url;
  }
}
